import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useClients, useCreateZETender } from "../hooks";

/** Pierwszy komunikat z koperty { data, errors, meta }, albo null gdy jej nie ma.
 *  Backend odrzuca m.in. przetarg z wykonawstwem ("P+W …" — portfel ZE prowadzi tylko
 *  projekty) i bez tego użytkownik zobaczyłby samo „nie udało się", bez powodu. */
function serverError(err: unknown): string | null {
  const body = (err as { response?: { data?: { errors?: unknown } } })?.response?.data
    ?.errors;
  if (!body) return null;
  if (typeof body === "string") return body;
  const first = Object.values(body as Record<string, unknown>)[0];
  const msg = Array.isArray(first) ? first[0] : first;
  return typeof msg === "string" ? msg : null;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function ZETenderCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { data: clients } = useClients();
  const create = useCreateZETender();
  const [title, setTitle] = useState("");
  const [client, setClient] = useState("");
  const [deadline, setDeadline] = useState("");
  const [reference, setReference] = useState("");
  const [operator, setOperator] = useState("");

  const ready = !!title.trim() && !!client && !!deadline;

  const submit = () => {
    if (!ready) return;
    create.mutate(
      {
        title: title.trim(),
        client,
        offer_deadline: new Date(deadline).toISOString(),
        reference_number: reference.trim() || undefined,
        operator_name: operator.trim() || undefined,
      },
      { onSuccess: (t) => onCreated(t.id) },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card w-full max-w-md shadow-hard-red" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            Nowy przetarg ZE
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Tytuł">
            <input className="input-base" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Klient">
            <select className="input-base" value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">— wybierz —</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Termin oferty">
            <input
              type="datetime-local"
              className="input-base"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </Field>
          <Field label="Zakład energetyczny / operator (opcjonalnie)">
            <input className="input-base" value={operator} onChange={(e) => setOperator(e.target.value)} />
          </Field>
          <Field label="Nr referencyjny (opcjonalnie)">
            <input className="input-base" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          {create.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">
              {serverError(create.error) ?? "Nie udało się utworzyć przetargu."}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
            <Button size="sm" disabled={!ready} loading={create.isPending} onClick={submit}>
              Utwórz
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
